import { type ReactNode, useState } from 'react';
import {
  Box,
  Button as MantineButton,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconDots, IconLink, IconX } from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import type { Client } from 'src/admin/users/types';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { useDeleteCalendarEventMutation } from 'src/meetings/hooks/useDeleteCalendarEventMutation';
import { UnrecognisedAttendees } from 'src/meetings/detail/components/UnrecognisedAttendees';
import { NonClientMeetingLink } from './NonClientMeetingLink';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import { useFileUpload } from 'src/data-capture/hooks/file-uploads/useFileUpload';
import { useFamilyFileDetections } from 'src/data-capture/hooks/file-uploads/useFamilyFileDetections';
import { MeetingPlatformIcon } from 'src/meetings/components/MeetingPlatformIcon';
import { useBotStatus } from 'src/meetings/hooks/useBotStatus';
import { useEvent } from 'src/meetings/hooks/useEvent';
import { useForceBotJoin } from 'src/meetings/hooks/useForceBotJoin';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import {
  assignSpeakerPath,
  meetingScopeFromParams,
  meetingsBasePath,
  type MeetingScope,
} from 'src/meetings/scope';
import { formatSpeakerLabel, getUnidentifiedSpeakers } from 'src/meetings/utils/speaker-utils';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { formatMeetingDuration, isPastEvent } from 'src/meetings/utils/date-utils';
import {
  getAllClientParticipants,
  getFirstClientParticipant,
  getParticipantDisplayNames,
} from 'src/meetings/utils/event-utils';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import FamilyConfirmModal from './FamilyConfirmModal';
import { UnidentifiedSpeakersWarningModal } from './UnidentifiedSpeakersWarningModal';
import { useGenerateMeetingNoteWithDefaultSource } from './useGenerateMeetingNoteWithDefaultSource';
import { resolveMeetingOverviews } from './meetingOverviews';
import { MeetingPreparation } from './MeetingPreparation';
import { ParticipantPill } from './ParticipantPill';
import { PastMeetingTabs } from './PastMeetingTabs';
import { SuggestedActionCard } from './SuggestedActionCard';
import { SuggestedActions } from './SuggestedActions';

export default function MeetingDetailsScreen() {
  const {
    familyId,
    clientId,
    eventId = '',
    eventTab,
  } = useParams<{ familyId?: string; clientId?: string; eventId: string; eventTab?: string }>();
  const navigate = useNavigate();
  const scope: MeetingScope = meetingScopeFromParams({ familyId, clientId });
  const { event: meeting, isLoading, refetch } = useEvent(eventId);
  const {
    deleteCalendarEvent,
    error: deleteError,
    isLoading: isDeleting,
  } = useDeleteCalendarEventMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { family } = useFamily(familyId);
  const clientFamilyMap = useClientFamilyMap();
  const { clients, isLoading: isClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const { client: individualClient } = useClient(clientId);
  const { forceBotJoin, isLoading: isBotJoinLoading } = useForceBotJoin(eventId);
  const { botStatus, isLoading: isBotStatusLoading } = useBotStatus(eventId);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [speakerWarningOpen, setSpeakerWarningOpen] = useState(false);
  const [unreviewedProfileUpdate, setUnreviewedProfileUpdate] = useState<{
    id: string;
    firstName: string;
  } | null>(null);

  const meetingsPath = meetingsBasePath(scope);
  const isFamilyScope = scope.kind === 'family';
  const isHub = scope.kind === 'all';

  // The hub carries no familyId in the route, so resolve the meeting's family from a
  // client participant. This lets a multi-client meeting be titled after the family
  // (as on the family view) rather than after a single member.
  const leadClientParticipantId =
    scope.kind === 'all' && meeting
      ? getFirstClientParticipant(meeting)?.participant.client?.id
      : undefined;
  const { client: leadClient } = useClient(leadClientParticipantId);
  const { family: participantFamily } = useFamily(leadClient?.familyId);

  // A finished meeting with a ready transcript can have a meeting note generated.
  const isPast = meeting ? isPastEvent(meeting) : false;
  const { transcript } = useTranscript({
    eventId,
    enabled: isPast && !!meeting?.hasTranscription,
  });
  const transcriptReady = transcript?.status === TranscriptionStatus.READY;
  const { meetingNote } = useCalendarEventMeetingNote(eventId, {
    enabled: isPast && transcriptReady,
  });
  const noteReady = meetingNote?.status === MeetingNoteStatus.READY;
  const noteExistsOrGenerating =
    noteReady || meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING;
  // Only offer to generate a note when the transcript is ready and there isn't one already.
  const canCreateNote = isPast && transcriptReady && !noteExistsOrGenerating;

  const { generate: generateNote, isGenerating } = useGenerateMeetingNoteWithDefaultSource(
    eventId,
    !!meeting?.hasMeetingAgenda,
  );

  // A meeting note's detections are reviewed per family member; we hide a member's
  // "update profile" suggestion once their detection row is processed. A family
  // recording's file lives under the family, not any member's client, so resolve
  // that family whether the meeting is opened in family scope or via the hub —
  // mirroring the review/finalise navigation. Without it a hub-opened family
  // meeting never reads the processed flag and the suggestion never clears.
  const dataUploadFileId = transcript?.dataUploadFileId;
  const fileFamilyId = scope.kind === 'family' ? scope.familyId : participantFamily?.id;
  const { members: familyDetectionMembers } = useFamilyFileDetections(
    fileFamilyId,
    dataUploadFileId,
    { enabled: isPast && noteReady },
  );
  const { file: recordingFile } = useFileUpload(dataUploadFileId, {
    clientId: scope.kind === 'individual' ? scope.clientId : undefined,
    enabled: isPast && noteReady && scope.kind === 'individual',
  });

  // Address and phone live on the full client's personalDetails, which the family
  // clients list omits, so fetch each participant's full record for those fields.
  const participantClientIds = meeting
    ? getAllClientParticipants(meeting)
        .map(p => p.participant.client?.id)
        .filter((id): id is string => !!id)
    : [];
  const participantClientQueries = useQueries({
    queries: participantClientIds.map(id => ({ queryKey: ['clients', id], enabled: !!id })),
  });
  const fullClientById = Object.fromEntries(
    participantClientQueries
      .map(result => result.data as Client | undefined)
      .filter((client): client is Client => !!client)
      .map(client => [client.id, client]),
  );

  // The scope's client list backs the ownership guard below, so wait for it
  // before deciding whether a family meeting belongs to this family.
  if ((isLoading && !meeting) || (isFamilyScope && isClientsLoading)) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (!meeting) {
    return <Text c="gray.6">This meeting could not be found.</Text>;
  }

  const meetingUrl = meeting.meetingUrl;
  const enrichedClients = individualClient ? [...clients, individualClient] : clients;
  const clientById = Object.fromEntries(enrichedClients.map(client => [client.id, client]));

  const clientParticipants = getAllClientParticipants(meeting)
    .map(p => p.participant.client)
    .filter((client): client is NonNullable<typeof client> => client !== null);

  // useEvent fetches any event by id, and the backend only isolates by tenant,
  // so a scoped route must confirm the meeting actually belongs to that family
  // or client before rendering it inside their shell.
  const belongsToScope =
    scope.kind === 'all' ||
    (scope.kind === 'individual' &&
      clientParticipants.some(participant => participant.id === scope.clientId)) ||
    (scope.kind === 'family' &&
      clientParticipants.some(participant => clients.some(client => client.id === participant.id)));

  if (!belongsToScope) {
    return <Text c="gray.6">This meeting could not be found.</Text>;
  }

  const participantNames = getParticipantDisplayNames(meeting, clientFamilyMap);

  const participantInfo = clientParticipants.map(simple => {
    const full = clientById[simple.id];
    const fullDetails = fullClientById[simple.id];
    return {
      id: simple.id,
      firstName: simple.firstName,
      name: `${simple.firstName} ${simple.lastName}`.trim(),
      address: fullDetails?.personalDetails.address ?? null,
      email: full?.emailAddress ?? simple.emailAddress ?? null,
      phone: fullDetails?.personalDetails.phoneNumber ?? null,
    };
  });

  const resolvedClientIds = new Set<string>();
  if (fileFamilyId) {
    for (const member of familyDetectionMembers) {
      if (member.isProcessed) resolvedClientIds.add(member.client.id);
    }
  } else if (scope.kind === 'individual' && recordingFile?.isResolved) {
    resolvedClientIds.add(scope.clientId);
  }
  const unresolvedParticipants = participantInfo.filter(p => !resolvedClientIds.has(p.id));

  const unidentifiedSpeakers = transcriptReady ? getUnidentifiedSpeakers(transcript?.segments) : [];

  // A recorded meeting with more than one client participant is a family
  // recording: its data-upload file lives under the family, not any single
  // member's client. Reviewing a member's detections must carry that familyId
  // (below) so the page hits the family endpoint; without it the file 404s for
  // every member who doesn't own it, surfacing the generic error page.
  const isFamilyMeeting = clientParticipants.length > 1;
  const recordingFamily = isFamilyScope ? family : participantFamily;
  const isFamilyRecording = isFamilyScope || (isFamilyMeeting && !!recordingFamily);

  let reviewFamilyId: string | undefined;
  if (scope.kind === 'family') {
    reviewFamilyId = scope.familyId;
  } else if (isFamilyRecording) {
    reviewFamilyId = recordingFamily?.id;
  }

  // Show a single family overview when every client attendee belongs to the same
  // family (the family route always resolves to its own family); otherwise each
  // attendee gets their own individual overview, so unrelated clients are never
  // grouped under one family overview.
  const routeFamily = isFamilyScope && family ? { id: family.id, name: family.name } : null;
  const overviews = resolveMeetingOverviews(
    participantInfo.map(participant => ({ id: participant.id, name: participant.name })),
    clientFamilyMap,
    routeFamily,
  );

  // The same preparation view is shown before a meeting and under a past
  // meeting's pre-meeting tab, so build it once and reuse it in both places.
  const meetingPreparation = (
    <MeetingPreparation
      overviews={overviews}
      participantInfo={participantInfo}
      scope={scope}
      meeting={meeting}
    />
  );

  // On the hub a meeting can have no Afternoon client attached at all. There is
  // no client to prepare for or generate notes from, so those meetings get the
  // link-a-client panel instead of the standard prep/past body.
  const isClientlessHub = isHub && clientParticipants.length === 0;

  const openMeeting = () => {
    if (meetingUrl) window.open(meetingUrl, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notifications.show({
        title: 'Link copied',
        message: 'Meeting link has been copied to clipboard',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to copy link',
        message: 'Please try again',
        color: 'red',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCalendarEvent(eventId);
      setShowDeleteModal(false);
      navigate(meetingsPath);
    } catch {
      // Error is captured by the mutation and shown in ConfirmDeleteModal.
    }
  };

  const hubActions = isHub ? (
    <Group gap="xs" wrap="nowrap">
      <Button
        variant="secondary"
        size="sm"
        rightSection={<IconLink size={16} />}
        onClick={handleCopyLink}
      >
        Copy link
      </Button>
      <Menu position="bottom-end">
        <Menu.Target>
          <MantineButton variant="default" px="xs">
            <IconDots size={16} />
          </MantineButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => setShowDeleteModal(true)}>Delete event</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  ) : null;

  const handleForceBotJoin = async () => {
    try {
      await forceBotJoin();
      notifications.show({
        title: 'Bot joining meeting',
        message: 'The meeting bot will join within 10 seconds',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to schedule bot',
        message: 'Could not reschedule the bot to join now. Please try again.',
        color: 'red',
      });
    }
  };

  const handleJoinMeetingClick = () => {
    const minutesFromStart =
      (Date.now() - new Date(meeting.scheduledStartAt).getTime()) / 1000 / 60;

    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CLICKED, {
      botStatus: botStatus || 'UNKNOWN',
      minutesFromStart: Math.abs(minutesFromStart),
      meetingUrl,
    });

    if (
      botStatus === 'ACTIVE' ||
      botStatus === 'NONE' ||
      botStatus === 'FINISHED' ||
      Math.abs(minutesFromStart) <= 2
    ) {
      openMeeting();
      return;
    }

    setRescheduleOpen(true);
  };

  const handleConfirmJoin = async (rescheduleBot: boolean) => {
    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CONFIRMED, { rescheduleBot, meetingUrl });
    if (rescheduleBot) await handleForceBotJoin();
    setRescheduleOpen(false);
    openMeeting();
  };

  const handleAddBotClick = async () => {
    Mixpanel.track(MixpanelEvents.BOT_MANUALLY_ADDED_TO_MEETING);
    await handleForceBotJoin();
  };

  const handleReviewDetections = (participantClientId: string) => {
    const params = new URLSearchParams({
      fileId: transcript?.dataUploadFileId ?? '',
      fileName: meeting.title,
      guid: uuidv4(),
      originMeetingId: meeting.id,
    });
    if (reviewFamilyId) params.set('familyId', reviewFamilyId);
    navigate(`/client/${participantClientId}/detected-entities?${params.toString()}`);
  };

  const handleUpdateProfile = (participant: { id: string; firstName: string }) => {
    if (meetingNote?.isReviewed) {
      handleReviewDetections(participant.id);
    } else {
      setUnreviewedProfileUpdate(participant);
    }
  };

  const dateTimePill = (
    <Flex align="center" p="xxs" bg="gray.1" style={{ borderRadius: 12, width: 'fit-content' }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
        {formatSchedule(meeting)}
      </Text>
    </Flex>
  );

  const handleGenerateNote = () => {
    if (unidentifiedSpeakers.length > 0) {
      setSpeakerWarningOpen(true);
    } else {
      void generateNote();
    }
  };

  // Suggested actions are additive, not exclusive: generating the note comes
  // first (behind a warning when speakers are unidentified), then identifying
  // speakers, then reviewing member profiles once a note exists.
  const suggestedActionCards: ReactNode[] = [];
  if (canCreateNote) {
    suggestedActionCards.push(
      <SuggestedActionCard
        key="generate-note"
        header="Generate"
        name="Meeting note"
        onClick={handleGenerateNote}
      />,
    );
  }
  suggestedActionCards.push(
    ...unidentifiedSpeakers.map(speaker => (
      <SuggestedActionCard
        key={`identify:${speaker.label}`}
        header="Identify"
        name={formatSpeakerLabel(speaker.label)}
        onClick={() => navigate(assignSpeakerPath(scope, meeting.id, speaker.label))}
      />
    )),
  );
  if (noteReady) {
    suggestedActionCards.push(
      ...unresolvedParticipants.map(participant => (
        <SuggestedActionCard
          key={`update:${participant.id}`}
          header="Update"
          name={participant.name}
          onClick={() =>
            handleUpdateProfile({
              id: participant.id,
              firstName: participant.firstName,
            })
          }
        />
      )),
    );
  }
  const suggestedActions: ReactNode =
    suggestedActionCards.length > 0 ? (
      <SuggestedActions>{suggestedActionCards}</SuggestedActions>
    ) : null;

  const handleClientLinked = async () => {
    await refetch();
  };

  let pastBody: ReactNode;
  if (isClientlessHub) {
    // A recorded meeting can still gain a client; one that was never recorded
    // has nothing to attach, so it keeps the old terminal message.
    pastBody = meeting.hasTranscription ? (
      <NonClientMeetingLink event={meeting} onLinked={handleClientLinked} context="recorded" />
    ) : (
      <Box mt="xl">
        <Text c="gray.6">
          This meeting does not have transcription, summary, or notes because it was not linked to a
          client.
        </Text>
      </Box>
    );
  } else {
    pastBody = (
      <>
        {suggestedActions && <Box mt="sm">{suggestedActions}</Box>}
        <Box mt="lg">
          <PastMeetingTabs
            meeting={meeting}
            transcript={transcript}
            initialTab={eventTab}
            scope={scope}
            preMeetingContent={meetingPreparation}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        {isPast ? (
          <Flex align="center" justify="space-between" gap="xxs">
            <Flex align="center" gap="xxs">
              <IconButton
                size={36}
                bg="gray.1"
                c="gray.9"
                aria-label="Back to meetings"
                onClick={() => navigate(meetingsPath)}
                style={{ borderRadius: 12 }}
              >
                <IconArrowLeft size={20} />
              </IconButton>
              {dateTimePill}
              <ParticipantPill
                participantNames={participantNames}
                onOpen={() => setAttendeesOpen(true)}
                radius={12}
                textSize="textSm"
                nameColor="gray.10"
                iconSize={20}
                px="xs"
                py="xxs"
                gap={8}
              />
            </Flex>
            {hubActions}
          </Flex>
        ) : (
          <Flex justify="space-between" align="flex-start" gap="sm">
            {dateTimePill}
            <Flex align="center" gap="xxs">
              {hubActions}
              <IconButton
                size={40}
                bg="gray.1"
                c="gray.9"
                aria-label="Close"
                onClick={() => navigate(meetingsPath)}
              >
                <IconX size={20} />
              </IconButton>
            </Flex>
          </Flex>
        )}

        <Flex align="center" mt="xs" py={5}>
          {meeting.meetingPlatform && (
            <Box style={{ paddingRight: 12 }}>
              <MeetingPlatformIcon platform={meeting.meetingPlatform} size={28} />
            </Box>
          )}
          <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {meeting.title}
          </Text>
        </Flex>

        {!isClientlessHub && (
          <Box mt="lg">
            <UnrecognisedAttendees event={meeting} onChanged={handleClientLinked} />
          </Box>
        )}

        {isPast ? (
          pastBody
        ) : (
          <>
            <Flex justify="space-between" align="center" mt="sm" gap="sm">
              <ParticipantPill
                participantNames={participantNames}
                onOpen={() => setAttendeesOpen(true)}
                radius={12}
                textSize="textSm"
                nameColor="gray.10"
                iconSize={20}
              />
              <Group gap="sm" wrap="nowrap">
                <Button
                  variant="primary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  disabled={!meetingUrl || isBotJoinLoading}
                  onClick={handleJoinMeetingClick}
                >
                  Join meeting
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  loading={isBotJoinLoading || isBotStatusLoading}
                  disabled={botStatus === 'ACTIVE' || !meetingUrl}
                  onClick={handleAddBotClick}
                >
                  {botStatus === 'ACTIVE' ? 'Bot recording' : 'Add bot'}
                </Button>
              </Group>
            </Flex>

            <Divider mt="md" color="gray.1" />

            {isClientlessHub ? (
              <NonClientMeetingLink
                event={meeting}
                onLinked={handleClientLinked}
                context="upcoming"
              />
            ) : (
              <>
                <Text mt="lg" size="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
                  Meeting preparation
                </Text>

                <Box mt="sm">{meetingPreparation}</Box>
              </>
            )}
          </>
        )}
      </Box>

      <AddExpectedAttendeesModal
        opened={attendeesOpen}
        onClose={() => setAttendeesOpen(false)}
        scope={scope}
        meeting={meeting}
      />

      <ConfirmDeleteModal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete event"
        message={`Are you sure you want to delete "${meeting.title}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />

      <UnidentifiedSpeakersWarningModal
        opened={speakerWarningOpen}
        count={unidentifiedSpeakers.length}
        isGenerating={isGenerating}
        onClose={() => setSpeakerWarningOpen(false)}
        onIdentify={() => {
          const firstLabel = unidentifiedSpeakers[0]?.label;
          setSpeakerWarningOpen(false);
          if (firstLabel) navigate(assignSpeakerPath(scope, meeting.id, firstLabel));
        }}
        onContinue={async () => {
          await generateNote();
          setSpeakerWarningOpen(false);
        }}
      />

      <FamilyConfirmModal
        opened={!!unreviewedProfileUpdate}
        onClose={() => setUnreviewedProfileUpdate(null)}
        onConfirm={() => {
          if (unreviewedProfileUpdate) handleReviewDetections(unreviewedProfileUpdate.id);
          setUnreviewedProfileUpdate(null);
        }}
        title="Meeting note isn't reviewed."
        description={`Are you sure you want to proceed and update ${unreviewedProfileUpdate?.firstName ?? ''}'s profile with unreviewed data?`}
        confirmLabel="Continue anyway"
        cancelLabel="Back to review"
        isLoading={false}
      />

      <Modal
        opened={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        title="Reschedule meeting bot?"
        centered
      >
        <Text size="textMd" mb="xs">
          Do you want to reschedule the bot to join the meeting immediately?
        </Text>
        <Text size="textSm" c="gray.6" mb="lg">
          If the meeting is starting early or you want the bot to join now, select &quot;Yes&quot;.
          Otherwise, the bot will join at the scheduled time.
        </Text>
        <Group justify="flex-end" gap="sm">
          <MantineButton
            variant="subtle"
            onClick={() => handleConfirmJoin(false)}
            disabled={isBotJoinLoading}
          >
            No, just join
          </MantineButton>
          <MantineButton
            variant="filled"
            onClick={() => handleConfirmJoin(true)}
            loading={isBotJoinLoading}
          >
            Yes, reschedule bot
          </MantineButton>
        </Group>
      </Modal>
    </>
  );
}

function formatSchedule(meeting: FamilyMeeting): string {
  const startIso = meeting.startAt ?? meeting.scheduledStartAt;
  const endIso = meeting.endAt ?? meeting.scheduledEndAt;
  const start = new Date(startIso);
  const end = new Date(endIso);

  const date = start.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

  const startMeridiem = start.getHours() >= 12 ? 'PM' : 'AM';
  const endMeridiem = end.getHours() >= 12 ? 'PM' : 'AM';
  const startText = formatClock(start, startMeridiem !== endMeridiem);
  const endText = formatClock(end, true);

  return `${date}, ${startText}-${endText} (${formatMeetingDuration(startIso, endIso)})`;
}

function formatClock(date: Date, withMeridiem: boolean): string {
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const meridiem = date.getHours() >= 12 ? 'PM' : 'AM';
  return withMeridiem ? `${hours}:${minutes}${meridiem}` : `${hours}:${minutes}`;
}
